import React, { useEffect, useState } from 'react';
import {
    Badge,
    Button,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
} from 'reactstrap';
import Swal from 'sweetalert2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faExclamationTriangle,
    faUserCheck,
    faUserPlus,
} from '@fortawesome/free-solid-svg-icons';
import Requester from '../../shared/Requester';

function formatFechaConsulta(iso) {
    if (!iso) {
        return '—';
    }
    try {
        return new Date(iso).toLocaleString('es-MX', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    } catch (_) {
        return String(iso);
    }
}

/**
 * Alerta de posible homonimia / registro PLD previo.
 * Si no se pasa `resumen`, consulta GET /pld/resumen al abrir (nombreCompleto requerido).
 */
export default function HomonimiaModal({
    isOpen,
    onClose,
    nombreCompleto,
    resumen: resumenProp,
    onFusionar,
    onCrearNuevo,
}) {
    const [resumen, setResumen] = useState(resumenProp || null);
    const [cargando, setCargando] = useState(false);

    useEffect(() => {
        if (!isOpen) {
            setResumen(resumenProp || null);
            return;
        }
        if (resumenProp) {
            setResumen(resumenProp);
            return;
        }
        const nombre =
            typeof nombreCompleto === 'string' ? nombreCompleto.trim() : '';
        if (!nombre) {
            setResumen(null);
            return;
        }
        let cancel = false;
        (async () => {
            setCargando(true);
            try {
                const res = await Requester.get('pld/resumen', {
                    params: { nombreCompleto: nombre },
                });
                if (!cancel) {
                    setResumen(res && res.data ? res.data : null);
                }
            } catch (_) {
                if (!cancel) setResumen(null);
            } finally {
                if (!cancel) setCargando(false);
            }
        })();
        return () => {
            cancel = true;
        };
    }, [isOpen, nombreCompleto, resumenProp]);

    async function handleFusionar() {
        if (typeof onFusionar === 'function') {
            await onFusionar(resumen);
            onClose();
            return;
        }
        await Swal.fire({
            icon: 'info',
            title: 'Fusionar roles',
            text: 'La consolidación de roles en el expediente se gestiona desde la operación (participantes). Este registro PLD ya existe en el sistema.',
        });
        onClose();
    }

    async function handleCrearNuevo() {
        if (typeof onCrearNuevo === 'function') {
            await onCrearNuevo(resumen);
            onClose();
            return;
        }
        await Swal.fire({
            icon: 'info',
            title: 'Nuevo sujeto',
            text: 'Si se trata de una persona distinta, continúe con la ficha actual; en expedientes distintos puede repetirse el nombre sin duplicar la identidad en PLD.',
        });
        onClose();
    }

    const etiquetaListado =
        resumen && resumen.listado === true
            ? 'Figura en listado'
            : resumen && resumen.listado === false
              ? 'Sin coincidencia en listado'
              : 'Estado de listado no disponible';

    return (
        <Modal isOpen={isOpen} toggle={onClose} size="lg" backdrop="static">
            <ModalHeader toggle={onClose} className="bg-warning text-white">
                <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
                Posible homonimia / registro PLD previo
            </ModalHeader>
            <ModalBody>
                {cargando ? (
                    <p className="text-muted mb-0">Consultando resumen PLD…</p>
                ) : (
                    <>
                        <p className="text-dark">
                            El servicio de resumen PLD indica que ya hay información
                            asociada a este nombre en la firma (UIF/SAT/ONU o en personas).
                        </p>
                        <div className="bg-light border rounded p-3 mb-3 d-flex align-items-start">
                            <div className="mr-3 text-warning">
                                <FontAwesomeIcon icon={faUserCheck} size="2x" />
                            </div>
                            <div>
                                <div className="small text-muted text-uppercase font-weight-bold">
                                    Nombre consultado
                                </div>
                                <div className="h5 text-uppercase mb-1">
                                    {nombreCompleto || '—'}
                                </div>
                                <div className="small text-muted mb-2">
                                    Última consulta registrada:{' '}
                                    <strong>
                                        {formatFechaConsulta(
                                            resumen && resumen.requestDate
                                        )}
                                    </strong>
                                </div>
                                <Badge color="secondary" pill className="text-uppercase">
                                    {etiquetaListado}
                                </Badge>
                            </div>
                        </div>
                        <p className="font-weight-bold border-bottom pb-2 mb-3">
                            ¿Qué acción desea tomar?
                        </p>
                    </>
                )}
            </ModalBody>
            <ModalFooter className="d-flex flex-wrap justify-content-between">
                <Button color="light" type="button" onClick={onClose}>
                    Cerrar
                </Button>
                <div>
                    <Button
                        color="primary"
                        type="button"
                        className="mr-2 mb-2"
                        disabled={cargando}
                        onClick={handleFusionar}
                    >
                        <FontAwesomeIcon icon={faUserCheck} className="mr-2" />
                        Fusionar roles
                    </Button>
                    <Button
                        color="secondary"
                        type="button"
                        className="mb-2"
                        disabled={cargando}
                        onClick={handleCrearNuevo}
                    >
                        <FontAwesomeIcon icon={faUserPlus} className="mr-2" />
                        Crear nuevo sujeto
                    </Button>
                </div>
            </ModalFooter>
        </Modal>
    );
}
